import React, { useEffect, useState, Suspense } from 'react';
import { Facebook as LoadingState } from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Button from 'components/buttons/Button';
import SectionWrapper from 'pages/Layout/SectionWrapper';
import { Heard, HeardMobile } from './Styled';
import GroupAdminCard, { GroupCard } from './GroupAdminCard/GroupAdminCard';
import Input from 'components/inputs/Input';

import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { getGroupsList } from 'services/rest/Group/getGroupsList';
import { sortGroups } from 'helpers/groupsHelper';
import { SuccessAlert } from 'components/Alert';
import MetricsGroupsSectionAdmin from './MetricsGroupsSectionAdmin';
import {
  GroupListWrapper,
  FiltersWrapper,
  StyledSelect,
  ControlsWrapper,
  InputWrapper
} from './Styled';
import TablePagination from '@mui/material/TablePagination';
import DropdownSelect from 'components/inputs/DropdownSelect/DropdownSelect';

const GroupAdminPage = () => {
  const { t } = useTranslation(['group_page', 'group_section']);
  const history = useHistory();
  const location = useLocation();
  const { state } = location as any;
  const { currentUser } = useSelector(selectUser);

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<GroupCard[]>([]);
  const [backendSuccess, setBackendSuccess] = useState<string | null>(state);

  const [filterParticipants, setFilterParticipants] = useState('all');
  const [sortType, setSortType] = useState('az');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [filterText, setFilterText] = useState('');

  const getData = async () => {
    const { data } = await getGroupsList(currentUser?.token, 500, 1);
    setGroups(sortGroups(data.data.docs));
    showAlertSuccessGroupCreated();
    setLoading(false);
  };

  const participantOptions = [
    { label: t('group_page:status_all'), value: 'all' },
    { label: t('group_page:MoreThan50'), value: 'majority of participants' },
    { label: t('group_page:LessThan50'), value: 'lessThan50' }
  ];

  const selectedParticipant =
    participantOptions.find(option => option.value === filterParticipants) ||
    participantOptions[0];

  const orderOptions = [
    { label: t('group_page:A-Z'), value: 'az' },
    { label: t('group_page:Z-A'), value: 'za' },
    { label: t('group_page:MostParticipants'), value: 'mostParticipants' },
    { label: t('group_page:LeastParticipants'), value: 'leastParticipants' }
  ];

  const selectedOrder =
    orderOptions.find(option => option.value === sortType) || orderOptions[0];

  const showAlertSuccessGroupCreated = () => {
    const queryParams = new URLSearchParams(location.search);
    const groupCreated = queryParams.get('backend_success_group_created');
    if (groupCreated != null && groupCreated === 'true') {
      setBackendSuccess(t('group_section:created_group'));
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const filteredGroups = groups.filter(group => {
    const matchesParticipants =
      filterParticipants === 'all' ||
      (filterParticipants === 'moreThan50' && group.totalAmbassadors > 50) ||
      (filterParticipants === 'lessThan50' && group.totalAmbassadors <= 50);

    const matchesText = group.name
      .toLowerCase()
      .includes(filterText.toLowerCase());

    return matchesParticipants && matchesText;
  });

  const sortedGroups = [...filteredGroups].sort((a, b) => {
    if (sortType === 'az') return a.name.localeCompare(b.name);
    if (sortType === 'za') return b.name.localeCompare(a.name);
    if (sortType === 'mostParticipants')
      return (b.totalAmbassadors || 0) - (a.totalAmbassadors || 0);
    if (sortType === 'leastParticipants')
      return (a.totalAmbassadors || 0) - (b.totalAmbassadors || 0);
    return 0;
  });

  const todosGroup = groups.find(group => group.isAll);
  const displayGroups = sortedGroups.filter(group => !group.isAll);
  const allVisibleGroups = todosGroup
    ? [todosGroup, ...displayGroups]
    : displayGroups;

  const paginatedGroups =
    rowsPerPage > 0
      ? allVisibleGroups.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )
      : allVisibleGroups;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return <LoadingState height="230px" width="100%" />;
  }

  return (
    <>
      {backendSuccess && (
        <SuccessAlert
          text={backendSuccess}
          allowClose={true}
          onClose={() => setBackendSuccess(null)}
        />
      )}

      <Suspense fallback={<LoadingState height="230px" width="100%" />}>
        <SectionWrapper>
          <Heard>
            <h1>{t('group_page:groups')}</h1>
            <Button
              className="hide-on-mobile"
              value={t('group_page:createGroup')}
              variant="primary"
              onClick={() => history.replace({ pathname: `/admin/group/new` })}
            />
          </Heard>

          <HeardMobile>
            <Button
              className="show-on-mobile"
              value={t('group_page:createGroup')}
              variant="primary"
              onClick={() => history.replace({ pathname: `/admin/group/new` })}
            />
          </HeardMobile>

          <MetricsGroupsSectionAdmin />

          <GroupListWrapper>
            <h1>{t('group_page:Group_List')}</h1>

            <ControlsWrapper>
              <InputWrapper>
                <Input
                  type="search"
                  placeholder={t('group_page:Group_name')}
                  value={filterText}
                  onChange={e => {
                    setFilterText(e.target.value);
                    setPage(0);
                  }}
                />
              </InputWrapper>

              <FiltersWrapper>
                <label>{t('group_page:filter')}</label>
                <DropdownSelect
                  options={participantOptions}
                  value={selectedParticipant}
                  onSelect={option => {
                    setFilterParticipants(option.value);
                    setPage(0);
                  }}
                />

                <label>{t('group_page:SortBy')}</label>
                <DropdownSelect
                  options={orderOptions}
                  value={selectedOrder}
                  onSelect={option => {
                    setSortType(option.value);
                    setPage(0);
                  }}
                />
              </FiltersWrapper>
            </ControlsWrapper>

            {paginatedGroups.map((group, index) => (
              <GroupAdminCard
                key={index}
                id={group.id}
                name={group.name}
                totalAmbassadors={group.totalAmbassadors}
                connectedAmbassadors={group.connectedAmbassadors}
                totalReach={group.totalReach}
                isAll={group.isAll}
              />
            ))}

            <TablePagination
              rowsPerPageOptions={[10, 50, 100]}
              component="div"
              count={allVisibleGroups.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </GroupListWrapper>
        </SectionWrapper>
      </Suspense>
    </>
  );
};

export default GroupAdminPage;
